/**
 * YCC Theme — assets/js/src/navigation.js
 *
 * Consolidated navigation behaviors:
 *  1. Primary site navigation (mobile toggle, submenu/mega toggle)
 *  2. Legacy header/nav interactions previously in nav.js
 *
 * @package YCC
 */

(function () {
  "use strict";

  initPrimaryNavigation();
  initLegacyNavigation();

  function initPrimaryNavigation() {
    const toggle = document.querySelector(".site-nav__toggle");
    const nav = document.querySelector(".site-nav--primary");

    if (!toggle || !nav) return;

    const isMobile = () => window.innerWidth < 1024;

    function closeAllOpen(container, except = null) {
      container.querySelectorAll(".is-open").forEach(function (item) {
        if (item !== except) {
          item.classList.remove("is-open");
          const link = item.querySelector(":scope > a");
          if (link) link.setAttribute("aria-expanded", "false");
          const button = item.querySelector(":scope > .submenu-toggle");
          if (button) button.setAttribute("aria-expanded", "false");
        }
      });
    }

    function toggleParentItem(item, control) {
      const willOpen = !item.classList.contains("is-open");

      Array.from(item.parentElement.children)
        .filter((el) => el !== item)
        .forEach((sib) => {
          sib.classList.remove("is-open");
          const sibLink = sib.querySelector(":scope > a");
          if (sibLink) sibLink.setAttribute("aria-expanded", "false");
          const sibButton = sib.querySelector(":scope > .submenu-toggle");
          if (sibButton) sibButton.setAttribute("aria-expanded", "false");
        });

      item.classList.toggle("is-open", willOpen);
      control.setAttribute("aria-expanded", willOpen ? "true" : "false");
    }

    const regularParentItems = nav.querySelectorAll(".menu-item-has-children");

    regularParentItems.forEach(function (item, index) {
      const link = item.querySelector(":scope > a");
      const submenuWrap = item.querySelector(":scope > .sub-menu-wrap");
      if (!link || !submenuWrap) return;

      if (!submenuWrap.id) {
        const itemId = item.id ? item.id : String(index);
        submenuWrap.id = "sub-menu-wrap-" + itemId;
      }

      link.setAttribute("aria-haspopup", "true");

      const button = document.createElement("button");
      button.type = "button";
      button.className = "submenu-toggle";
      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-controls", submenuWrap.id);
      button.setAttribute("aria-label", "Toggle submenu");
      item.prepend(button);

      button.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleParentItem(item, button);
      });

      button.addEventListener("keydown", function (e) {
        if ((e.key === "Enter" || e.key === " ") && isMobile()) {
          e.preventDefault();
          toggleParentItem(item, button);
        }

        if (e.key === "ArrowDown") {
          e.preventDefault();
          item.classList.add("is-open");
          button.setAttribute("aria-expanded", "true");

          const firstFocusable = item.querySelector(
            ".sub-menu-wrap a, .sub-menu a",
          );
          if (firstFocusable) firstFocusable.focus();
        }
      });
    });

    const megaParentItems = nav.querySelectorAll(".has-mega-menu");

    megaParentItems.forEach(function (item) {
      const link = item.querySelector(":scope > a");
      if (!link) return;

      link.setAttribute("aria-expanded", "false");
      link.setAttribute("aria-haspopup", "true");

      link.addEventListener("click", function (e) {
        const href = link.getAttribute("href");
        const isPlaceholder =
          !href || href === "#" || href.startsWith("javascript");

        if (isMobile() || isPlaceholder) {
          e.preventDefault();
          toggleParentItem(item, link);
        }
      });

      link.addEventListener("keydown", function (e) {
        const href = link.getAttribute("href");
        const isPlaceholder =
          !href || href === "#" || href.startsWith("javascript");

        if ((e.key === "Enter" || e.key === " ") && isPlaceholder) {
          e.preventDefault();
          toggleParentItem(item, link);
        }

        if (e.key === "ArrowDown") {
          e.preventDefault();
          item.classList.add("is-open");
          link.setAttribute("aria-expanded", "true");

          const firstFocusable = item.querySelector(
            ".mega-menu__panel a, .sub-menu-wrap a, .sub-menu a",
          );
          if (firstFocusable) firstFocusable.focus();
        }
      });
    });

    toggle.addEventListener("click", function () {
      const isOpen = nav.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      if (!isOpen) closeAllOpen(nav);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;

      nav.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      closeAllOpen(nav);
      toggle.focus();
    });

    document.addEventListener("click", function (e) {
      if (!nav.contains(e.target) && !toggle.contains(e.target)) {
        nav.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
        closeAllOpen(nav);
      }
    });

    nav.addEventListener("focusout", function (e) {
      if (e.relatedTarget && !nav.contains(e.relatedTarget)) {
        closeAllOpen(nav);
      }
    });
  }

  function initLegacyNavigation() {
    const header = document.querySelector("header");

    function isHamburgerHidden() {
      const hamburger = document.querySelector(".hamburger");
      if (!hamburger) return false;
      return window.getComputedStyle(hamburger).display === "none";
    }

    // Search toggle
    const search = document.querySelector("header .search");
    if (search) {
      search.addEventListener("click", (e) => {
        if (e.target.closest(".action")) {
          search.classList.toggle("active");
        }
      });
    }

    // Prevent popup click propagation
    document.querySelectorAll(".headerpopup").forEach((el) => {
      el.addEventListener("click", (e) => e.stopPropagation());
    });

    // Click popup toggle
    header?.addEventListener("click", (e) => {
      const popup = e.target.closest(".haspopup");
      if (!popup) return;

      if (!isHamburgerHidden()) {
        e.stopPropagation();
        popup.classList.toggle("active");
      }
    });

    // Mouseover popup
    header?.addEventListener("mouseover", (e) => {
      const popup = e.target.closest(".haspopup");
      if (!popup) return;

      if (isHamburgerHidden()) {
        document
          .querySelectorAll(".haspopup")
          .forEach((el) => el.classList.remove("active"));
        popup.classList.add("active");
      }
    });

    // Popup mouseout
    document.querySelectorAll("header .headerpopup").forEach((popup) => {
      popup.addEventListener("mouseout", function () {
        const username = this.querySelector("input[name='username']");
        if (username && document.activeElement === username) return;

        this.closest(".active")?.classList.remove("active");
      });
    });

    // Nav hover behaviour
    document.querySelectorAll("#nav > .wp-block-group").forEach((group) => {
      group.addEventListener("mouseover", function () {
        this.querySelectorAll(
          ".wp-block-group__inner-container > .wp-block-group",
        ).forEach((el) => el.classList.add("container"));

        document.querySelectorAll("#nav img").forEach((img) => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.srcset = img.dataset.srcset || "";
            img.dataset.src = "";
          }
        });

        document
          .querySelectorAll("#nav > .wp-block-group")
          .forEach((el) => el.classList.remove("active"));

        this.classList.add("active");
      });
    });

    // Inner menu mouseout
    document
      .querySelectorAll(".wp-block-group__inner-container > .wp-block-group")
      .forEach((group) => {
        group.addEventListener("mouseout", function () {
          this.closest(".wp-block-group.active")?.classList.remove("active");
        });
      });

    // Grid filters toggle
    document.querySelectorAll(".grid-list li").forEach((li) => li.remove());

    const gridList = document.querySelector(".grid-list");
    if (gridList) {
      const toggle = document.createElement("li");
      toggle.className = "filterstoggle";
      gridList.appendChild(toggle);

      gridList.addEventListener("click", (e) => {
        if (e.target.closest(".filterstoggle")) {
          document.querySelector(".filters")?.classList.toggle("open");
          toggle.classList.toggle("open");
        }
      });
    }

    // Hamburger
    const hamburger = document.querySelector(".hamburger");
    hamburger?.addEventListener("click", () => {
      document.querySelector("#body")?.classList.toggle("navopen");
    });

    // Mobile nav click
    document
      .querySelectorAll(".navbar-container #nav > .wp-block-group")
      .forEach((group) => {
        group.addEventListener("click", function (e) {
          document.querySelectorAll("#nav img").forEach((img) => {
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.srcset = img.dataset.srcset || "";
              img.dataset.src = "";
            }
          });

          if (document.querySelector("#body")?.classList.contains("navopen")) {
            e.stopPropagation();

            const thisOpen = this.classList.contains("open");

            document
              .querySelectorAll("#nav > .wp-block-group")
              .forEach((el) => el.classList.remove("open"));

            if (!thisOpen) this.classList.add("open");
          }
        });
      });
  }
})();
